import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { authApi, type AuthResponse } from '../api/auth.api'
import { AuthContext } from './auth-context'
import {
  clearSessionUser,
  getStoredSessionUser,
  storeSessionUser,
  type SessionUser,
} from './session-store'
import { AUTH_UNAUTHORIZED_EVENT, authEvents, tokenStorage } from '@/shared/api/auth'
import { queryClient } from '@/shared/api/query-client'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(getStoredSessionUser)

  // The API client fires this when a request is unauthorized and the token
  // refresh failed - the session is dead, clear the local state. Route
  // guards react to `isAuthenticated` flipping and redirect to /login.
  useEffect(() => {
    const onUnauthorized = () => {
      clearSessionUser()
      queryClient.clear()
      setUser(null)
    }
    authEvents.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized)
    return () => authEvents.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized)
  }, [])

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      signIn: (auth: AuthResponse) => {
        tokenStorage.set({ accessToken: auth.accessToken, refreshToken: auth.refreshToken })
        const sessionUser: SessionUser = {
          userId: auth.userId,
          username: auth.username,
          displayName: auth.displayName,
          avatarUrl: auth.avatarUrl,
          userRole: auth.userRole,
        }
        storeSessionUser(sessionUser)
        setUser(sessionUser)
      },
      updateUsername: (username: string) => {
        setUser((current) => {
          if (!current) return current
          const updated = { ...current, username }
          storeSessionUser(updated)
          return updated
        })
      },
      signOut: () => {
        // Best effort: revoke the token server-side, but never block the UI.
        void authApi.logout().catch(() => {})
        tokenStorage.clear()
        clearSessionUser()
        queryClient.clear()
        setUser(null)
      },
    }),
    [user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
